import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Home,
  Percent,
  Users,
  MessagesSquare,
  Circle,
  Menu,
  Wallet,
} from "lucide-react"; // Added Wallet icon
import config from "../config";

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalUserBalance, setTotalUserBalance] = useState(0); // New state for total user balance
  const [smsOrders, setSmsOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Helper function to format balance
  const formatBalance = (amount, symbol = "₦") => {
    if (!amount && amount !== 0) return "";
    const rounded = Math.ceil(amount * 100) / 100; // round up to 2 decimals
    return `${symbol}${rounded.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Effect 1: Fetch user profile and check admin status
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("authToken");

        if (!token) {
          console.log("No auth token found");
          navigate("/login");
          return;
        }

        const res = await axios.get(`${config.API_BASE_URL}/api/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setProfile(res.data);

        // 🚨 If the user is NOT an admin, redirect them to user dashboard
        if (res.data.role !== "admin") {
          console.log("Non-admin user accessing admin page, redirecting...");
          navigate("/dashboard");
          return;
        }

        console.log("Admin user verified:", res.data.email);
      } catch (err) {
        console.error("❌ Error fetching profile:", err);

        if (err.response?.status === 401) {
          console.log("Token invalid or expired");
          toast.error("Session expired. Please login again.");
          localStorage.removeItem("authToken");
        } else if (err.response?.status === 403) {
          toast.error("Access forbidden");
        } else {
          toast.error("Error loading profile");
        }

        navigate("/login");
      } finally {
        setCheckingAuth(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  // Effect 2: Fetch SMS orders (admin only)
  useEffect(() => {
    const fetchSmsOrders = async () => {
      if (!profile || profile.role !== "admin") {
        return;
      }

      try {
        const token = localStorage.getItem("authToken");

        const res = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchAllOrders`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setSmsOrders(res.data.orders || []);
      } catch (err) {
        console.error("Error fetching SMS orders:", err);
        toast.error("Failed to load SMS orders");
      }
    };

    fetchSmsOrders();
  }, [profile]);

  // Effect 3: Fetch orders and calculate profit (admin only)
  useEffect(() => {
    const fetchOrdersAndProfit = async () => {
      if (!profile || profile.role !== "admin") {
        return;
      }

      try {
        const token = localStorage.getItem("authToken");

        const res = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchAllOrders`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const allOrders = res.data.orders || [];
        setOrders(allOrders);

        const completedOrders = allOrders.filter(
          (order) => order.status === "completed",
        );

        let profitSum = 0;

        completedOrders.forEach((order) => {
          let percentage = 0;

          if (typeof order.profit === "string") {
            const match = order.profit.match(/percentage:\s*(\d+)/);
            if (match) {
              percentage = Number(match[1]);
            }
          }

          if (typeof order.profit === "object" && order.profit?.percentage) {
            percentage = order.profit.percentage;
          }

          const profitAmount =
            order.price - order.price / (1 + percentage / 100);

          profitSum += profitAmount;
        });

        setTotalProfit(profitSum);
      } catch (error) {
        console.error("Error fetching orders:", error);
        toast.error("Failed to load profit data");
      }
    };

    fetchOrdersAndProfit();
  }, [profile]);

  // Effect 4: Fetch users and calculate total balance
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/api/users`, {
          withCredentials: true,
        });

        const allUsers = response.data;

        // Calculate total balance of all users
        const totalBalance = allUsers.reduce((sum, user) => {
          return sum + (user.wallet?.balance || 0);
        }, 0);

        setTotalUserBalance(totalBalance);

        // Fetch ACTIVE users
        const activeRes = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchActiveUsers`,
          { withCredentials: true },
        );

        const activeList = activeRes.data.users || [];
        const activeIds = new Set(activeList.map((u) => u._id));

        const finalUsers = allUsers.map((user) => ({
          ...user,
          isActive: activeIds.has(user._id),
        }));

        setUsers(finalUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  if (checkingAuth) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-600">
        Checking admin access...
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 text-gray-800 overflow-hidden">
      {/* Sidebar */}
      <div
        className={`fixed md:static top-0 left-0 h-full bg-white shadow-lg w-64 p-5 flex flex-col transition-all duration-300 z-50
      ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <h1 className="text-2xl font-bold mb-8 text-indigo-600">Admin Panel</h1>

        <nav className="space-y-3">
          {/* Home */}
          <Link
            to="/admin/dashboard"
            className={`flex items-center gap-3 px-4 py-2 rounded-lg w-full transition-all duration-200 ${
              location.pathname === "/admin/dashboard"
                ? "bg-indigo-50 text-indigo-600"
                : "text-gray-700 hover:bg-indigo-50"
            }`}
          >
            <Home className="w-5 h-5" />
            Home
          </Link>

          {/* Profit Percentage */}
          <Link
            to="/admin/percentage"
            className={`flex items-center gap-3 px-4 py-2 rounded-lg w-full transition-all duration-200 ${
              location.pathname === "/admin/percentage"
                ? "bg-indigo-50 text-indigo-600"
                : "text-gray-700 hover:bg-indigo-50"
            }`}
          >
            <Percent className="w-5 h-5" />
            Profit Percentage
          </Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-y-auto relative">
        {/* Mobile Header */}
        <div className="md:hidden p-4 bg-white shadow flex items-center justify-between sticky top-0 z-40">
          <h2 className="text-lg font-semibold">Dashboard</h2>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 bg-indigo-600 text-white rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Desktop Title */}
        <div className="hidden md:flex justify-between items-center p-6">
          <h2 className="text-3xl font-bold">Dashboard</h2>
        </div>

        {/* Summary Cards - Now 4 cards in 2x2 grid on medium screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 px-4 md:px-10 mb-8">
          {/* Total Users */}
          <div className="bg-white shadow rounded-xl p-6 flex items-center justify-between mt-5 md:mt-0">
            <div>
              <h3 className="text-gray-500 text-sm font-medium">Total Users</h3>
              <p className="text-3xl font-bold text-indigo-600">
                {users.length}
              </p>
            </div>
            <div className="bg-indigo-100 text-indigo-600 p-3 rounded-full">
              <Users className="w-6 h-6" />
            </div>
          </div>

          {/* Total SMS Orders */}
          <div className="bg-white shadow rounded-xl p-6 flex items-center justify-between">
            <div>
              <h3 className="text-gray-500 text-sm font-medium">
                Total SMS Orders
              </h3>
              <p className="text-3xl font-bold text-indigo-600">
                {smsOrders.length}
              </p>
            </div>
            <div className="bg-indigo-100 text-indigo-600 p-3 rounded-full">
              <MessagesSquare className="w-6 h-6" />
            </div>
          </div>

          {/* Total Profit */}
          <div className="bg-white shadow rounded-xl p-6 flex items-center justify-between">
            <div>
              <h3 className="text-gray-500 text-sm font-medium">
                Total Profit
              </h3>
              <p className="text-3xl font-bold text-green-600">
                {formatBalance(totalProfit)}
              </p>
            </div>
            <div className="bg-green-100 text-green-600 p-3 rounded-full">
              <Circle className="w-6 h-6" />
            </div>
          </div>

          {/* NEW: Total User Balance */}
          <div className="bg-white shadow rounded-xl p-6 flex items-center justify-between">
            <div>
              <h3 className="text-gray-500 text-sm font-medium">
                Total User Balance
              </h3>
              <p className="text-3xl font-bold text-purple-600">
                {formatBalance(totalUserBalance)}
              </p>
            </div>
            <div className="bg-purple-100 text-purple-600 p-3 rounded-full">
              <Wallet className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* User Table */}
        <div className="bg-white shadow rounded-xl p-6 mx-4 md:mx-10 mb-10">
          <h3 className="text-lg font-semibold mb-4">User List</h3>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 rounded-lg">
              <thead>
                <tr className="bg-gray-50 text-left text-sm font-semibold text-gray-600">
                  <th className="px-4 py-3 border-b">#</th>
                  <th className="px-4 py-3 border-b">Name</th>
                  <th className="px-4 py-3 border-b">Email</th>
                  <th className="px-4 py-3 border-b">Balance</th>
                  <th className="px-4 py-3 border-b">Joined</th>
                </tr>
              </thead>

              <tbody>
                {users.length > 0 ? (
                  users.map((user, index) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 border-b">{index + 1}</td>
                      <td className="px-4 py-3 border-b">
                        {user.firstName || "Unknown"} {user.lastName || ""}
                      </td>
                      <td className="px-4 py-3 border-b">{user.email}</td>
                      <td className="px-4 py-3 border-b">
                        {formatBalance(user.wallet?.balance)}
                      </td>
                      <td className="px-4 py-3 border-b text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center py-4 text-gray-500 border-b"
                    >
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
